import React, {useEffect, useState} from "react";
import {
    DynamicChart,
    Grid,
    SmartSelect,
    chartAxisFormatEnums as defaultFormatters,
    dataset,
    Skeleton,
    Typography
} from "@leap4/frontend";

import moment from "moment";
import {IAdServerTagMetric, IGraphTimeBucket} from "@leap4/entities-discrepancy-common";
import {getLabelsForGraph, graphDataProgressions, TimeDisplayInterval} from "../charts/charts";

const optionalCompares = [
    {name: 'Revenue Comparison Values', value: 'revenue-values'},
    {name: 'Revenue Comparison Percent', value: 'revenue-percent'},
    {name: 'Impressions Values', value: 'impressions-values'},
    {name: 'Impressions Percent', value: 'impressions-percent'},
]


export interface ICustomDataSet {
    labelName: string,
    metrics: IAdServerTagMetric[],
}

type DiscrepancyChartProps = {
    isLoading: boolean,
    graphData: IGraphTimeBucket[],
    startDate: string,
    endDate: string
}
export default function DiscrepancyChart({
                                             isLoading,
                                             graphData,
                                             startDate,
                                             endDate
                                         }: DiscrepancyChartProps) {
    const [labelsForGraph, setLabelsForGraph] = useState(null)
    const [selectedGraphDataProgressionIdx, setGraphDataProgressionIdx] = useState(0);
    const [selectedCompareIdx, setSelectedCompareIdx] = useState(0);
    const [datasets, setDatasets] = useState([])
    const [xAxisFormat, setXAxisFormat] = useState(null)
    const [yAxisFormat, setYAxisFormat] = useState(defaultFormatters.USD)

    const generateChartProps = () => {
        if (!graphData) return;

        const labels = getLabelsForGraph(startDate, endDate, TimeDisplayInterval.Daily)

        setLabelsForGraph(labels)
        generateDatasetsAndAxisFormat()
    }

    useEffect(generateChartProps, [graphData]);

    const fillMissingDates = () => {
        let filledGraphData = [...graphData];
        const labels = getLabelsForGraph(startDate, endDate, TimeDisplayInterval.Daily)

        for (const label of labels) {
            const labelDateStr = new Date(label).toISOString().split('T')[0];

            const timelyDataForLabel = filledGraphData.find(timelyData => {
                return new Date(timelyData.timestamp).toISOString().split('T')[0] === labelDateStr;
            });

            if (!timelyDataForLabel) {
                filledGraphData.push({
                    timestamp: new Date(labelDateStr),
                    connectedTags: {
                        revenue: 0,
                        impressions: 0,
                        cost: 0,
                    },
                    publisher: {
                        revenue: 0,
                        impressions: 0,
                    },
                    discrepancy: {
                        revenue: {
                            percent: 0,
                            value: 0,
                        },
                        impressions: {
                            percent: 0,
                            value: 0,
                        },
                    },
                });
            }
        }

        filledGraphData.sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());

        return filledGraphData;
    };

    const generateDatasetsAndAxisFormat = () => {
        if (!graphData) {
            return;
        }

        const filledGraphData = fillMissingDates();
        const type = graphDataProgressions[selectedGraphDataProgressionIdx].value
        let selectedMetric = null;
        let datasets: dataset[] = []
        let values = []
        switch (selectedCompareIdx) {
            case 0:
                selectedMetric = 'revenue';
                values = filledGraphData.map((timelyData) => timelyData.connectedTags[selectedMetric])
                let adServerRevenueDataset: dataset = {type, labelName: 'Adserver Revenue Totals', values}

                values = filledGraphData.map((timelyData) => timelyData.publisher[selectedMetric])
                let publisherRevenueDataset = {type, labelName: 'Publisher Revenue Totals', values}

                datasets = [adServerRevenueDataset, publisherRevenueDataset]
                setYAxisFormat(defaultFormatters.USD)
                break;

            case 1:
                selectedMetric = 'revenue';

                values = filledGraphData.map((timelyData) => timelyData.discrepancy ? timelyData.discrepancy[selectedMetric]?.percent : '')

                let revenuePercentDataset: dataset = {labelName: 'Revenue Percent Discrepancy', values, type}

                datasets = [revenuePercentDataset]
                setYAxisFormat(defaultFormatters.PERCENT)
                break;

            case 2:
                selectedMetric = 'impressions';

                values = filledGraphData.map((timelyData) => timelyData.connectedTags[selectedMetric])
                let adServerImpressionsDataset = {labelName: 'Adserver Impressions Totals', values, type}

                values = filledGraphData.map((timelyData) => timelyData.publisher[selectedMetric]);
                let publisherImpressionsDataset = {labelName: 'Publisher Impressions Totals', values, type}

                datasets = [adServerImpressionsDataset, publisherImpressionsDataset];
                setYAxisFormat(defaultFormatters.NUMBER)
                break;

            case 3:
                const labelName = 'Impressions Percent Discrepancy'
                selectedMetric = 'impressions';
                values = filledGraphData.map((timelyData) => timelyData.discrepancy ? timelyData.discrepancy[selectedMetric].percent : '')
                let impressionsPercentDataset: dataset = {labelName, values, type}
                datasets = [impressionsPercentDataset,]
                setYAxisFormat(defaultFormatters.PERCENT)
                break;
        }

        const formatByHalfHourly = (timestamp) => moment(new Date(timestamp)).utc().format("HH:mm")
        if (startDate === endDate) {
            setXAxisFormat(prevState => formatByHalfHourly)
        } else {
            setXAxisFormat(null)
        }

        setDatasets(datasets)
    }

    useEffect(() => {
        generateDatasetsAndAxisFormat()
    }, [selectedCompareIdx, selectedGraphDataProgressionIdx]);


    return (
        <>
            {!isLoading && !datasets.length &&
                <Typography variant={"overline"}> Error with getting the data...</Typography>}

            {isLoading ? <Skeleton sx={{borderRadius: "16px"}} variant={"rounded"} height={"100%"} animation={"wave"}/> :
                <DynamicChart datasets={datasets} labels={labelsForGraph} axisFormat={{x: xAxisFormat, y: yAxisFormat}}
                              title={"Comparison Graph"}>
                    <Grid container flexWrap={"nowrap"} gap={'15px'}>
                        <Grid item>
                            <SmartSelect
                                selectProps={{
                                    size: "small", autoWidth: true,
                                }}
                            items={optionalCompares} selectedItemInd={selectedCompareIdx}
                            setSelectedState={(val) => setSelectedCompareIdx(val)}
                            title={"Comparsion"}/>
                        </Grid>

                        <Grid item>
                            <SmartSelect
                                selectProps={{size: "small"}}
                                items={graphDataProgressions}
                                selectedItemInd={selectedGraphDataProgressionIdx}
                                setSelectedState={(val) => setGraphDataProgressionIdx(val)}
                                title={"Data Type"}/>
                        </Grid>

                    </Grid>
                </DynamicChart>

            }

        </>

    )

}